import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Users, 
  UserPlus, 
  Wallet, 
  Sparkles,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Copy,
  Check
} from 'lucide-react';
import { difficultDecisions } from '../../data/toolsData';

const iconMap = {
  Users: Users,
  UserPlus: UserPlus,
  Wallet: Wallet,
  Sparkles: Sparkles
};

const RadarTool = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedScenario, setExpandedScenario] = useState(null);
  const [copiedPhrase, setCopiedPhrase] = useState(null);

  const handleCopyPhrase = (phrase, index) => {
    navigator.clipboard.writeText(phrase.replace(/"/g, ''));
    setCopiedPhrase(index);
    setTimeout(() => setCopiedPhrase(null), 2000);
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/ferramentas')}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#6B4E37] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar às ferramentas
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#B5C4B1]/30 flex items-center justify-center mx-auto mb-6">
            <Users size={32} className="text-[#6B4E37]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {difficultDecisions.title}
          </h1>
          <p className="text-lg text-[#6B4E37] max-w-xl mx-auto">
            {difficultDecisions.subtitle}
          </p>
        </div>

        {/* Categories */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {difficultDecisions.categories.map((category) => {
            const Icon = iconMap[category.icon];
            const isActive = activeCategory === category.id;
            
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(isActive ? null : category.id)}
                className={`p-6 rounded-xl text-left transition-all duration-300 ${
                  isActive 
                    ? 'bg-white shadow-lg border-2' 
                    : 'bg-white/60 hover:bg-white/80 border-2 border-transparent'
                }`}
                style={{ borderColor: isActive ? category.color : 'transparent' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}30` }}
                    >
                      <Icon size={24} style={{ color: category.color }} />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#2C2C2C] text-lg">{category.name}</h3>
                      <p className="text-sm text-[#8B7D6B]">{category.scenarios.length} cenários</p>
                    </div>
                  </div>
                  {isActive ? <ChevronUp size={20} className="text-[#8B7D6B]" /> : <ChevronDown size={20} className="text-[#8B7D6B]" />}
                </div>
              </button>
            );
          })}
        </div>

        {/* Scenarios */}
        {activeCategory && (
          <div className="space-y-4 animate-fade-in">
            {difficultDecisions.categories
              .find(c => c.id === activeCategory)
              ?.scenarios.map((scenario) => {
                const isExpanded = expandedScenario === scenario.id;
                
                return (
                  <div 
                    key={scenario.id}
                    className="bg-white/80 backdrop-blur-sm rounded-xl overflow-hidden shadow-sm"
                  >
                    <button
                      onClick={() => setExpandedScenario(isExpanded ? null : scenario.id)}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-white/50 transition-colors"
                    >
                      <h4 className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C]">
                        {scenario.title}
                      </h4>
                      {isExpanded ? <ChevronUp size={20} className="text-[#8B7D6B]" /> : <ChevronDown size={20} className="text-[#8B7D6B]" />}
                    </button>

                    {isExpanded && (
                      <div className="px-6 pb-6 space-y-6 animate-fade-in">
                        {/* Situation */}
                        <div className="bg-[#F5F3F0] rounded-lg p-4">
                          <p className="text-[#6B4E37] italic">{scenario.situation}</p>
                        </div>

                        {/* Considerations */}
                        <div>
                          <h5 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-3">
                            A considerar
                          </h5>
                          <ul className="space-y-2">
                            {scenario.considerations.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-[#6B4E37]">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#C4B5A0] mt-2 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Suggestions */}
                        <div>
                          <h5 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-3">
                            Sugestões práticas
                          </h5>
                          <ul className="space-y-2">
                            {scenario.suggestions.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-[#6B4E37]">
                                <span className="w-5 h-5 rounded-full bg-[#B5C4B1]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <span className="text-xs font-medium text-[#6B4E37]">{idx + 1}</span>
                                </span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Phrases */}
                        <div>
                          <h5 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-3 flex items-center gap-2">
                            <MessageCircle size={16} />
                            Frases prontas
                          </h5>
                          <div className="space-y-2">
                            {scenario.phrases.map((phrase, idx) => (
                              <div 
                                key={idx}
                                className="bg-[#FAF9F7] border border-[#E8E4E0] rounded-lg p-4 flex items-start justify-between gap-4"
                              >
                                <p className="text-[#6B4E37] italic text-sm">{phrase}</p>
                                <button
                                  onClick={() => handleCopyPhrase(phrase, `${scenario.id}-${idx}`)}
                                  className="flex-shrink-0 p-2 hover:bg-[#E8E4E0] rounded-lg transition-colors"
                                  title="Copiar frase"
                                >
                                  {copiedPhrase === `${scenario.id}-${idx}` ? (
                                    <Check size={16} className="text-[#B5C4B1]" />
                                  ) : (
                                    <Copy size={16} className="text-[#8B7D6B]" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        {!activeCategory && (
          <div className="text-center py-12 text-[#8B7D6B]">
            <p>Selecione uma categoria acima para ver os cenários</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadarTool;
